import type { Booking } from "@/lib/bookings";
import type { AvailabilityBlock } from "@/lib/availability";
import { parseISODate, toISODate } from "@/pages/admin/format";

export type CalendarStatus = "available" | "limited" | "booked" | "fully" | "blocked" | "maintenance";

export interface DayInfo {
  date: Date;
  iso: string;
  bookings: Booking[];
  blocks: AvailabilityBlock[];
  status: CalendarStatus;
}

export const CALENDAR_STATUS_META: Record<
  CalendarStatus,
  { label: string; dot: string; chip: string; cell: string; text: string }
> = {
  available: {
    label: "Available",
    dot: "bg-secondary-400",
    chip: "bg-secondary-100 text-secondary-800 border-secondary-200",
    cell: "bg-background-50",
    text: "text-secondary-800",
  },
  limited: {
    label: "Limited",
    dot: "bg-accent-500",
    chip: "bg-accent-100 text-accent-900 border-accent-300",
    cell: "bg-accent-50",
    text: "text-accent-900",
  },
  booked: {
    label: "Booked",
    dot: "bg-primary-500",
    chip: "bg-primary-100 text-primary-700 border-primary-300",
    cell: "bg-primary-50",
    text: "text-primary-800",
  },
  fully: {
    label: "Fully Booked",
    dot: "bg-primary-800",
    chip: "bg-primary-100 text-primary-900 border-primary-300",
    cell: "bg-primary-100",
    text: "text-primary-900",
  },
  blocked: {
    label: "Blocked",
    dot: "bg-foreground-500",
    chip: "bg-foreground-100 text-foreground-700 border-foreground-200",
    cell: "bg-foreground-100/60",
    text: "text-foreground-700",
  },
  maintenance: {
    label: "Maintenance",
    dot: "bg-accent-600",
    chip: "bg-accent-100 text-accent-900 border-accent-600/30",
    cell: "bg-accent-100/50",
    text: "text-accent-900",
  },
};

export const CALENDAR_TARGETS: Record<string, string[]> = {
  farm: [
    "Sri Lakshmi Farm",
    "Green Valley Farm",
    "Konaseema Coconut Farm",
    "Godavari Riverside Farm",
    "Sunrise Paddy Fields",
    "Rythu Organic Farm",
  ],
  experience: [
    "Farmer for a Day",
    "Paddy Harvest Walk",
    "Konaseema Backwater Boat Ride",
    "Farm-to-Plate Lunch",
    "Bullock Cart Village Tour",
    "Sunrise Bird Watching",
  ],
};

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

export function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

export function startOfWeekMonday(date: Date): Date {
  const result = new Date(date);
  const offset = (result.getDay() + 6) % 7;
  result.setDate(result.getDate() - offset);
  result.setHours(0, 0, 0, 0);
  return result;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function buildMonthGrid(cursor: Date): Date[] {
  const first = startOfMonth(cursor);
  const gridStart = startOfWeekMonday(first);
  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
}

export function buildWeek(cursor: Date): Date[] {
  const start = startOfWeekMonday(cursor);
  return Array.from({ length: 7 }, (_, index) => addDays(start, index));
}

export function formatMonthTitle(date: Date): string {
  return date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

export function formatWeekTitle(date: Date): string {
  const start = startOfWeekMonday(date);
  const end = addDays(start, 6);
  const fmt = (value: Date) => value.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `${fmt(start)} – ${fmt(end)}`;
}

function computeStatus(bookingCount: number, blocks: AvailabilityBlock[]): CalendarStatus {
  if (blocks.length > 0) {
    const maintenance = blocks.some((block) => block.block_type === "maintenance");
    return maintenance ? "maintenance" : "blocked";
  }
  if (bookingCount === 0) return "available";
  if (bookingCount === 1) return "limited";
  if (bookingCount >= 4) return "fully";
  return "booked";
}

export function buildDayInfos(
  dates: Date[],
  bookings: Booking[],
  blocks: AvailabilityBlock[],
): DayInfo[] {
  const bookingsByDate = new Map<string, Booking[]>();
  bookings.forEach((booking) => {
    if (!booking.preferred_date) return;
    const key = booking.preferred_date.slice(0, 10);
    const list = bookingsByDate.get(key) || [];
    list.push(booking);
    bookingsByDate.set(key, list);
  });

  const blocksByDate = new Map<string, AvailabilityBlock[]>();
  blocks.forEach((block) => {
    const start = parseISODate(block.start_date);
    const end = parseISODate(block.end_date);
    const cursor = new Date(start);
    while (cursor <= end) {
      const key = toISODate(cursor);
      const list = blocksByDate.get(key) || [];
      list.push(block);
      blocksByDate.set(key, list);
      cursor.setDate(cursor.getDate() + 1);
    }
  });

  return dates.map((date) => {
    const key = toISODate(date);
    const dayBookings = bookingsByDate.get(key) || [];
    const dayBlocks = blocksByDate.get(key) || [];
    return {
      date,
      iso: key,
      bookings: dayBookings,
      blocks: dayBlocks,
      status: computeStatus(dayBookings.length, dayBlocks),
    };
  });
}